import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Building2, CircleCheck, GitBranch, Stethoscope } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { fetchCalls } from '../api';
import { INTENT_LABELS, SERVICE_AREA_LABELS } from '../config/endoTaxonomy';

const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#0ea5e9', '#7c3aed', '#64748b'];

const FUNNEL_STAGES = [
  {
    key: 'new_booking_request',
    title: 'Новые запросы',
    description: 'Клиенты с новой потребностью в записи или подборе услуги',
    filter: { call_type: 'new_booking_request' },
    count: (items) => items.filter((item) => item.callType === 'new_booking_request').length
  },
  {
    key: 'booking_progress',
    title: 'Есть движение к записи',
    description: 'Запись оформлена, назначен перезвон или лист ожидания',
    filter: { call_type: 'new_booking_request' },
    count: (items) => items.filter((item) => (
      item.callType === 'new_booking_request'
      && ['achieved', 'pending_specific_next_step'].includes(item.bookingOutcome)
    )).length
  },
  {
    key: 'booked',
    title: 'Запись оформлена',
    description: 'Новый визит зафиксирован в разговоре',
    filter: { call_type: 'new_booking_request', booking_outcome: 'achieved' },
    count: (items) => items.filter((item) => (
      item.callType === 'new_booking_request' && item.bookingOutcome === 'achieved'
    )).length
  }
];

const normalizeDateRange = (dateRange) => {
  if (!dateRange?.from || !dateRange?.to) return null;
  return {
    date_from: format(dateRange.from, 'yyyy-MM-dd'),
    date_to: format(dateRange.to, 'yyyy-MM-dd')
  };
};

const formatPercent = (value, total) => {
  if (!total) return '0%';
  return `${((value * 100) / total).toFixed(1)}%`;
};

const summarize = (items, keyGetter, labelGetter, limit = 8) => {
  const counts = new Map();
  const labels = new Map();
  items.forEach((item) => {
    const key = keyGetter(item);
    if (!key) return;
    counts.set(key, (counts.get(key) ?? 0) + 1);
    labels.set(key, labelGetter(item, key));
  });
  const total = items.length;
  return Array.from(counts.entries())
    .map(([key, count]) => ({
      key,
      label: labels.get(key) ?? key,
      count,
      total,
      valueLabel: `${count} (${formatPercent(count, total)})`
    }))
    .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label, 'ru'))
    .slice(0, limit);
};

const MiniBarChart = ({ title, description, icon: Icon, data, onSelect }) => {
  const maxCount = Math.max(1, ...data.map((item) => item.count));
  return (
    <Card>
      <CardHeader className="space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground">Нет данных для отображения.</p>
        ) : (
          <div className="space-y-3 rounded-md bg-muted/30 p-3">
            {data.map((item, index) => (
              <button
                key={item.key}
                type="button"
                onClick={() => onSelect?.(item)}
                className="block w-full rounded-md px-1 py-1 text-left transition-colors hover:bg-background/70 focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <div className="mb-1 flex items-start justify-between gap-3 text-xs">
                  <span className="min-w-0 text-muted-foreground">{item.label}</span>
                  <span className="shrink-0 font-semibold text-foreground">{item.valueLabel}</span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-background">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(4, (item.count * 100) / maxCount)}%`,
                      backgroundColor: COLORS[index % COLORS.length]
                    }}
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PlusFunnelCard = ({ items, onOpenCallJournal }) => {
  const total = FUNNEL_STAGES[0].count(items);
  const booked = FUNNEL_STAGES[2].count(items);
  const conversion = total ? (booked * 100) / total : 0;

  return (
    <Card>
      <CardHeader className="space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="h-5 w-5" />
          Воронка записи
        </CardTitle>
        <CardDescription>
          Конверсия новых запросов в оформленную запись: {conversion.toFixed(1)}%
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {FUNNEL_STAGES.map((stage) => {
          const calls = stage.count(items);
          const share = total ? (calls * 100) / total : 0;
          return (
            <button
              key={stage.key}
              type="button"
              onClick={() => onOpenCallJournal?.(stage.filter)}
              className="w-full rounded-md border bg-muted/20 p-3 text-left transition-colors hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{stage.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{stage.description}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-sm font-semibold">{calls.toLocaleString('ru-RU')}</div>
                  <div className="text-xs text-muted-foreground">{share.toFixed(1)}% от входа</div>
                </div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-gray-200 dark:bg-gray-800">
                <div
                  className="h-2 rounded-full bg-blue-600"
                  style={{ width: `${Math.max(4, Math.min(100, share))}%` }}
                />
              </div>
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
};

const PlusDashboardWidgets = ({ dateRange, callType = 'all', onOpenCallJournal }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const isFetching = useRef(false);

  const params = useMemo(() => {
    const range = normalizeDateRange(dateRange);
    if (!range) return null;
    return {
      ...range,
      call_type: callType === 'all' ? undefined : callType,
      limit: 500
    };
  }, [callType, dateRange]);

  const load = useCallback(async () => {
    if (!params || isFetching.current) return;
    setError(null);
    isFetching.current = true;
    try {
      const response = await fetchCalls(params);
      setItems(Array.isArray(response?.items) ? response.items : []);
    } catch {
      setError('Не удалось загрузить срезы Плюс.');
      setItems([]);
    } finally {
      isFetching.current = false;
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const topics = useMemo(
    () => summarize(
      items,
      (item) => item.serviceArea,
      (item, key) => SERVICE_AREA_LABELS[key] ?? key,
      8
    ),
    [items]
  );

  const callTypes = useMemo(
    () => summarize(
      items,
      (item) => item.callType,
      (item, key) => INTENT_LABELS[key] ?? key,
      8
    ),
    [items]
  );

  const lines = useMemo(
    () => summarize(
      items,
      (item) => item.employeeName,
      (item) => item.employeeName ?? 'Линия не указана',
      8
    ),
    [items]
  );

  return (
    <div className="grid gap-6">
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <PlusFunnelCard items={items} onOpenCallJournal={onOpenCallJournal} />

      <div className="grid gap-6 lg:grid-cols-2">
        <MiniBarChart
          title="Типы звонков"
          description="Операционный контур разговора"
          icon={CircleCheck}
          data={callTypes}
          onSelect={(item) => onOpenCallJournal?.({ call_type: item.key })}
        />
        <MiniBarChart
          title="Темы обращений"
          description="Медицинская или сервисная тема запроса"
          icon={Stethoscope}
          data={topics}
          onSelect={(item) => onOpenCallJournal?.({ service_area: item.key })}
        />
        <MiniBarChart
          title="Линии"
          description="Распределение звонков по линиям"
          icon={Building2}
          data={lines}
          onSelect={(item) => onOpenCallJournal?.({ department: item.key })}
        />
      </div>
    </div>
  );
};

export default PlusDashboardWidgets;
